import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { FaEye, FaTrash, FaFilePdf, FaCar, FaUser, FaCalendar, FaTools, FaUserTie } from 'react-icons/fa';
import { useSucursal } from '@/context/SucursalContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import { useAuth } from '@/context/AuthContextApi';
import toastService from '@/utils/toastService';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import MantenimientoDetailModal from './MantenimientoDetailModal';
import Card, { CardBody } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import CustomSelect from '@/components/common/CustomSelect';

// Estilos específicos para inputs de fecha en iOS/iPadOS
const dateInputStyles = `
  .date-input-ios {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }
  
  .date-input-ios::-webkit-date-and-time-value {
    width: 100%;
  }
  
  .date-input-ios::-webkit-datetime-edit {
    width: 100%;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = dateInputStyles;
  document.head.appendChild(styleSheet);
}

/**
 * Página principal de gestión de mantenimientos
 */
const MantenimientosPage = () => {
  const [mecanicos, setMecanicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Estado de datos
  const [mantenimientosOriginales, setMantenimientosOriginales] = useState([]);
  const [filteredMantenimientos, setFilteredMantenimientos] = useState([]);
  
  // Estado de filtros
  const [filters, setFilters] = useState({
    searchTerm: '',
    fecha_desde: '',
    fecha_hasta: '',
    mecanico_id: ''
  });
  
  // Modal de detalle
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [mantenimientoSeleccionado, setMantenimientoSeleccionado] = useState(null);

  const { sucursalData } = useSucursal();
  const { empresaActual, isSuperAdmin } = useSimpleEmpresa();
  const { user, isAdmin, clienteData, loading: authLoading } = useAuth();

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    // Para SuperAdmin: necesita empresa seleccionada
    // Para usuarios regulares: necesita sucursal y (ser admin o tener datos de cliente)
    const shouldLoad = isSuperAdmin 
      ? empresaActual?.id 
      : sucursalData && (isAdmin || clienteData);

    /*console.log('🔄 useEffect fetchData - shouldLoad:', shouldLoad, {
      isSuperAdmin,
      empresaActual: empresaActual?.id,
      sucursalData: sucursalData?.id,
      isAdmin,
      clienteData: clienteData?.id
    });*/

    if (shouldLoad) {
      fetchData();
      if (isAdmin || isSuperAdmin) {
        cargarMecanicos(); // Solo admin/SuperAdmin puede filtrar por mecánico
      }
    } else {
      // Si no debe cargar, asegurarse de limpiar el estado de loading
      setLoading(false);
      setMantenimientosOriginales([]);
    }
  }, [sucursalData, isAdmin, clienteData, isSuperAdmin, empresaActual]);

  // Aplicar filtros cuando cambian
  useEffect(() => {
    applyFilters();
  }, [mantenimientosOriginales, filters]);

  // Función para cargar datos desde la API
  const fetchData = async () => {
    setLoading(true);
    try {
      let filtrosBasicos = {};
      
      if (isSuperAdmin) {
        // SuperAdmin: cargar mantenimientos de todas las sucursales de la empresa
        if (!empresaActual?.id) {
          //console.log('SuperAdmin sin empresa seleccionada');
          setLoading(false);
          return;
        }
        
        //console.log('SuperAdmin - Cargando mantenimientos para empresa:', empresaActual.nombre);
        
        // Si hay sucursal específica, filtrar por ella, sino mostrar todos de la empresa
        if (sucursalData?.id) {
          filtrosBasicos.sucursal_id = sucursalData.id;
          //console.log('SuperAdmin - Filtrando por sucursal específica:', sucursalData.Nombre);
        } else {
          // Obtener todas las sucursales de la empresa
          // Por ahora, si no hay sucursal específica, usar empresa_id cuando esté disponible en API
          //console.log('SuperAdmin - Mostrando todos los mantenimientos de la empresa');
        }
      } else {
        // Usuario regular: necesita sucursal
        if (!sucursalData) {
          //console.log('Usuario regular sin sucursal seleccionada');
          setLoading(false);
          return;
        }
        
        //console.log('Cargando mantenimientos para sucursal:', sucursalData.Nombre);
        filtrosBasicos.sucursal_id = sucursalData.id;
        
        // Si es cliente, filtrar solo sus mantenimientos
        if (!isAdmin && clienteData?.id) {
          filtrosBasicos.cliente_id = clienteData.id;
          //console.log('Cliente - Filtrando mantenimientos para cliente ID:', clienteData.id);
        }
      }
      
      //console.log('Filtros enviados:', filtrosBasicos);
      const data = await mantenimientosApiService.getMantenimientos(filtrosBasicos);
      //console.log('✅ Mantenimientos recibidos:', data.length, data);
      setMantenimientosOriginales(data);
    } catch (error) {
      console.error('Error cargando mantenimientos:', error);
      toastService.error('Error al cargar los mantenimientos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar mecánicos para filtro
  const cargarMecanicos = async () => {
    try {
      //console.log('🔧 Cargando mecánicos - isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin);
      //console.log('🔧 sucursalData:', sucursalData);
      
      // Siempre requerir sucursal_id para cargar mecánicos
      if (!sucursalData?.id) {
        //console.log('🔧 Sin sucursal seleccionada, no se cargan mecánicos');
        setMecanicos([]);
        return;
      }
      
      const filtros = {
        sucursal_id: sucursalData.id
      };
      
      //console.log('🔧 Cargando mecánicos para sucursal:', sucursalData.id, '(', sucursalData.Nombre, ')');
      // Usar el servicio de mecánicos existente
      const { getMecanicos } = await import('@/services/mecanicosApiService');
      const data = await getMecanicos(filtros.sucursal_id);
      //console.log('🔧 Mecánicos cargados (activos e inactivos):', data.length, data);
      setMecanicos(data);
    } catch (error) {
      console.error('Error cargando mecánicos:', error);
      setMecanicos([]);
    }
  };

  // Aplicar filtros localmente
  const applyFilters = () => {
    //console.log('🔍 Aplicando filtros. Datos originales:', mantenimientosOriginales.length);
    //console.log('🔍 Filtros actuales:', filters);
    //console.log('🔍 Muestra de datos originales:', mantenimientosOriginales.slice(0, 2));
    
    let filtered = [...mantenimientosOriginales];

    // Filtro de búsqueda (patente, cliente)
    if (filters.searchTerm && filters.searchTerm.trim() !== '') {
      const searchLower = filters.searchTerm.toLowerCase().trim();
      //console.log('🔍 Aplicando filtro de búsqueda:', searchLower);
      
      const beforeSearch = filtered.length;
      filtered = filtered.filter(mantenimiento => {
        const patente = mantenimiento.patente?.toLowerCase() || '';
        const cliente = mantenimiento.citas?.clientes;
        const clienteNombre = cliente 
          ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.toLowerCase()
          : '';
        
        const matches = patente.includes(searchLower) || clienteNombre.includes(searchLower);
        if (!matches) {
          //console.log('❌ No match para:', { patente, clienteNombre, search: searchLower });
        }
        return matches;
      });
      //console.log(`🔍 Filtro búsqueda: ${beforeSearch} → ${filtered.length}`);
    }

    // Filtro por fecha desde
    if (filters.fecha_desde && filters.fecha_desde.trim() !== '') {
      //console.log('🔍 Aplicando filtro fecha desde:', filters.fecha_desde);
      const beforeDate = filtered.length;
      filtered = filtered.filter(mantenimiento => {
        const fechaMantenimiento = moment(mantenimiento.fecha);
        const fechaDesde = moment(filters.fecha_desde);
        const isAfter = fechaMantenimiento.isSameOrAfter(fechaDesde, 'day');
        if (!isAfter) {
          //console.log('❌ Fecha anterior:', { fecha: mantenimiento.fecha, desde: filters.fecha_desde });
        }
        return isAfter;
      });
      //console.log(`🔍 Filtro fecha desde: ${beforeDate} → ${filtered.length}`);
    }

    // Filtro por fecha hasta
    if (filters.fecha_hasta && filters.fecha_hasta.trim() !== '') {
      //console.log('🔍 Aplicando filtro fecha hasta:', filters.fecha_hasta);
      const beforeDate = filtered.length;
      filtered = filtered.filter(mantenimiento => {
        const fechaMantenimiento = moment(mantenimiento.fecha);
        const fechaHasta = moment(filters.fecha_hasta);
        const isBefore = fechaMantenimiento.isSameOrBefore(fechaHasta, 'day');
        if (!isBefore) {
          //console.log('❌ Fecha posterior:', { fecha: mantenimiento.fecha, hasta: filters.fecha_hasta });
        }
        return isBefore;
      });
      //console.log(`🔍 Filtro fecha hasta: ${beforeDate} → ${filtered.length}`);
    }

    // Filtro por mecánico
    if (filters.mecanico_id && filters.mecanico_id.trim() !== '') {
      //console.log('🔍 Aplicando filtro mecánico:', filters.mecanico_id);
      const beforeMecanico = filtered.length;
      filtered = filtered.filter(mantenimiento => {
        const matches = mantenimiento.mecanico_id === parseInt(filters.mecanico_id);
        if (!matches) {
          //console.log('❌ Mecánico no coincide:', { mecanico: mantenimiento.mecanico_id, filtro: filters.mecanico_id });
        }
        return matches;
      });
      //console.log(`🔍 Filtro mecánico: ${beforeMecanico} → ${filtered.length}`);
    }

    //console.log('✅ Datos filtrados finales:', filtered.length);
    setFilteredMantenimientos(filtered);
  };

  // Manejar búsqueda desde FilterBar
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setFilters(prev => ({
      ...prev,
      searchTerm: searchValue
    }));
  };

  // Manejar cambios en filtros adicionales
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Limpiar filtros
  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      fecha_desde: '',
      fecha_hasta: '',
      mecanico_id: ''
    });
  };

  // Ver detalle de mantenimiento
  const handleVerDetalle = (mantenimiento) => {
    setMantenimientoSeleccionado(mantenimiento);
    setDetailModalOpen(true);
  };

  // Eliminar mantenimiento - Solo admin y SuperAdmin
  const handleEliminar = async (mantenimiento) => {
    if (!isAdmin && !isSuperAdmin) {
      toastService.error('No tiene permisos para eliminar mantenimientos');
      return;
    }
    
    const confirmar = window.confirm(
      `¿Está seguro de eliminar el mantenimiento del vehículo ${mantenimiento.patente}?\n\nEsta acción no se puede deshacer y restaurará el stock de repuestos utilizados.`
    );
    
    if (!confirmar) return;
    
    try {
      await mantenimientosApiService.deleteMantenimiento(mantenimiento.id);
      toastService.success('Mantenimiento eliminado correctamente');
      fetchData(); // Recargar lista
    } catch (error) {
      console.error('Error eliminando mantenimiento:', error);
      toastService.error('Error al eliminar el mantenimiento');
    }
  };

  // Ver PDF
  const handleVerPDF = (mantenimiento) => {
    if (mantenimiento.pdf_base64) {
      // Convertir base64 a blob y abrir
      const base64Data = mantenimiento.pdf_base64.split(',')[1] || mantenimiento.pdf_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      toastService.warning('No hay PDF disponible para este mantenimiento');
    }
  };

  // Formatear precio
  const formatPrice = (price) => {
    const numero = parseFloat(price) || 0;
    return numero.toLocaleString('es-ES');
  };

  // Mostrar mensaje si no hay contexto necesario
  if (isSuperAdmin && !empresaActual) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center py-8">
          <p className="text-gray-400">Seleccione una empresa para ver los mantenimientos</p>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin && !sucursalData) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center py-8">
          <p className="text-gray-400">Seleccione una sucursal para ver los mantenimientos</p>
        </div>
      </div>
    );
  }

  // Si es cliente y no tiene datos de cliente, verificar si ya terminó de cargar
  if (!isAdmin && !isSuperAdmin && !clienteData) {
    if (authLoading) {
      // Aún está cargando los datos de autenticación
      return (
        <div className="p-4 sm:p-6">
          <div className="text-center py-8 bg-gray-800 rounded-lg">
            <p className="text-gray-400 mb-4">Cargando información del cliente...</p>
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </div>
      );
    } else {
      // Ya terminó de cargar pero no es un cliente registrado
      return (
        <div className="p-4 sm:p-6">
          <div className="text-center py-8 bg-gray-800 rounded-lg">
            <FaTools className="mx-auto text-gray-500 text-4xl mb-4" />
            <p className="text-gray-400 mb-2">No tiene acceso a esta sección</p>
            <p className="text-gray-500 text-sm">
              Esta cuenta no está registrada como cliente en el sistema.
              Contacte al administrador para obtener acceso.
            </p>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1">
          {(isAdmin || isSuperAdmin) ? 'Mantenimientos' : 'Mis Mantenimientos'}
        </h1>
        <p className="text-gray-400 text-sm md:text-base">
          {isSuperAdmin 
            ? `Gestión de mantenimientos - ${empresaActual?.nombre}${sucursalData ? ` - ${sucursalData.Nombre}` : ' (todas las sucursales)'}`
            : (isAdmin 
              ? `Gestión de mantenimientos realizados - ${sucursalData?.Nombre}`
              : `Historial de mantenimientos de sus vehículos - ${sucursalData?.Nombre}`
            )
          }
        </p>
      </div>

      {/* FilterBar */}
      <div className="mb-4 bg-gray-800 rounded-lg p-4">
        <div className={`grid grid-cols-1 gap-4 items-end ${(isAdmin || isSuperAdmin) ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          {/* Campo de búsqueda */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Buscar mantenimientos
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="w-full pl-10 h-10 px-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Buscar por patente o cliente..."
                value={filters.searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* Fecha Desde */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Fecha Desde
            </label>
            <input
              type="date"
              name="fecha_desde"
              value={filters.fecha_desde}
              onChange={handleFilterChange}
              className="date-input-ios w-full h-10 px-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Fecha Hasta
            </label>
            <input
              type="date"
              name="fecha_hasta"
              value={filters.fecha_hasta}
              onChange={handleFilterChange}
              className="date-input-ios w-full h-10 px-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Mecánico - Solo para admin y SuperAdmin */}
          {(isAdmin || isSuperAdmin) && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Mecánico
              </label>
              <CustomSelect
                name="mecanico_id"
                value={filters.mecanico_id}
                onChange={handleFilterChange}
                className="w-full md:w-auto bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="Todos"
                options={mecanicos.map(mecanico => ({
                  id: mecanico.id,
                  value: mecanico.id,
                  label: mecanico.nombre
                }))}
              />
            </div>
          )}

          {/* Botón limpiar filtros */}
          <div>
            <button
              onClick={handleResetFilters}
              className="w-full h-10 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-white transition-colors duration-200"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Mantenimientos */}
      {loading ? (
        <div className="p-8 text-center bg-gray-800 rounded-lg">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-2">Cargando mantenimientos...</p>
        </div>
      ) : filteredMantenimientos.length === 0 ? (
        <div className="p-8 text-center bg-gray-800 rounded-lg">
          <FaTools className="mx-auto text-gray-500 text-4xl mb-4" />
          <p className="text-gray-400">No se encontraron mantenimientos</p>
          <p className="text-gray-500 text-sm">Intente ajustar los filtros de búsqueda</p>
        </div>
      ) : (
          <>
            {isMobile ? (
              // Vista móvil - Cards
              <div className="space-y-3">
                {filteredMantenimientos.map((mantenimiento) => {
                  const cliente = mantenimiento.citas?.clientes;
                  const vehiculo = mantenimiento.citas?.vehiculo;
                  const mecanicoPrincipal = mantenimiento.mantenimiento_mecanicos?.[0]?.mecanicos;
                  
                  return (
                    <div 
                      key={mantenimiento.id} 
                      className="bg-gray-800 rounded-lg border-l-4 border-blue-500 overflow-hidden hover:bg-gray-700 cursor-pointer transition-all duration-200"
                      onClick={() => handleVerDetalle(mantenimiento)}
                    >
                      {/* Contenido de la card */}
                      <div className="p-4">
                        {/* Vehículo y Cliente */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <FaCar className="text-blue-400 mr-2" size={16} />
                              <PatenteDisplay patente={mantenimiento.patente} size="xs" />
                            </div>
                            {vehiculo && (
                              <div className="text-sm text-gray-400 mb-2">
                                {vehiculo.marca_local || vehiculo.marca} {vehiculo.modelo_local || vehiculo.modelo} ({vehiculo.año})
                              </div>
                            )}
                            {cliente && (
                              <div className="flex items-center">
                                <FaUser className="text-green-400 mr-2" size={14} />
                                <span className="text-sm text-white">
                                  {cliente.nombre} {cliente.apellido}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-bold text-white">
                              ${formatPrice(mantenimiento.total)}
                            </div>
                            <div className="text-xs text-gray-400">
                              IVA incluido
                            </div>
                          </div>
                        </div>
                        
                        {/* Fecha y Servicios */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <div className="flex items-center">
                              <FaCalendar className="text-yellow-400 mr-2" size={14} />
                              <span className="text-sm text-white">
                                {moment(mantenimiento.fecha).format('DD/MM/YYYY')}
                              </span>
                            </div>
                            {mantenimiento.hora_finalizacion && (
                              <div className="text-xs text-gray-400 ml-5">
                                {mantenimiento.hora_finalizacion}
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <div className="text-sm text-white">
                              {mantenimiento.mantenimiento_servicios?.length || 0} servicios
                            </div>
                            {mantenimiento.mantenimiento_repuestos?.length > 0 && (
                              <div className="text-xs text-gray-400">
                                {mantenimiento.mantenimiento_repuestos.length} repuestos
                              </div>
                            )}
                            {mantenimiento.mantenimiento_items_adicionales?.length > 0 && (
                              <div className="text-xs text-gray-400">
                                {mantenimiento.mantenimiento_items_adicionales.length} items adicionales
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Mecánico */}
                        <div className="text-gray-400 flex items-center justify-between">
                          <div className='flex items-center'>
                            <FaUserTie className=" text-purple-400 mr-2"/> 
                            <span className='text-white text-sm'>{mecanicoPrincipal?.nombre || 'Sin asignar'}</span>
                          </div>
                          {mantenimiento.mantenimiento_mecanicos?.length > 1 && (
                            <span className="text-gray-400">
                              {' '}+{mantenimiento.mantenimiento_mecanicos.length - 1} más
                            </span>
                          )}
                          <div className="text-right">
                            <div className="text-xs text-gray-400">
                              <Badge variant="success" size="xs">
                                Finalizado
                              </Badge>
                            </div>
                        </div>
                        </div>
                      </div>
                      
                      {/* Botones de acción móvil */}
                      <div className="flex justify-between border-t border-gray-600 bg-gray-800">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVerDetalle(mantenimiento);
                          }}
                          className="flex-1 py-3 flex flex-col items-center justify-center text-blue-400 hover:bg-gray-700"
                          title="Ver detalle"
                        >
                          <FaEye className="text-lg" />
                          <span className="text-xs mt-1">Ver</span>
                        </button>
                        
                        {mantenimiento.pdf_base64 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerPDF(mantenimiento);
                            }}
                            className="flex-1 py-3 flex flex-col items-center justify-center text-green-400 hover:bg-gray-700 border-l border-r border-gray-600"
                            title="Ver PDF"
                          >
                            <FaFilePdf className="text-lg" />
                            <span className="text-xs mt-1">PDF</span>
                          </button>
                        )}
                        
                        {(isAdmin || isSuperAdmin) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEliminar(mantenimiento);
                            }}
                            className="flex-1 py-3 flex flex-col items-center justify-center text-red-400 hover:bg-gray-700"
                            title="Eliminar"
                          >
                            <FaTrash className="text-lg" />
                            <span className="text-xs mt-1">Eliminar</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Vista escritorio - Cards
              <div className="space-y-3">
                {filteredMantenimientos.map((mantenimiento) => {
                  const cliente = mantenimiento.citas?.clientes;
                  const vehiculo = mantenimiento.citas?.vehiculo;
                  const mecanicoPrincipal = mantenimiento.mantenimiento_mecanicos?.[0]?.mecanicos;
                  
                  return (
                    <Card 
                      key={mantenimiento.id}
                      hover 
                      className="mb-3 border-l-4 border-blue-500 transition-all duration-300 hover:border-blue-400 cursor-pointer"
                      onClick={() => handleVerDetalle(mantenimiento)}
                    >
                      <CardBody className="p-0">
                        <div className="flex justify-between items-center p-4">
                          {/* Contenido principal */}
                          <div className="flex-grow">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 xl:gap-8">
                              {/* Columna 1: Vehículo */}
                              <div className="md:col-span-1 xl:col-span-1 flex items-center">
                                <div className="space-y-2">
                                  {/* Fila 1: Ícono y Patente */}
                                  <div className="flex items-center">
                                    <div className="mr-3 md:mr-2 xl:mr-4 bg-blue-600 bg-opacity-20 p-2 md:p-1.5 rounded-full">
                                      <FaCar className="text-blue-400" size={16} />
                                    </div>
                                    <div className="flex items-center gap-4 xl:gap-6">
                                      <div className="min-w-[100px] xl:min-w-[120px]">
                                        <PatenteDisplay patente={mantenimiento.patente} size="xs" />
                                      </div>

                                      <Badge variant="success" size="sm">
                                        Finalizado
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  {/* Fila 2: Información del vehículo */}
                                  {vehiculo && (
                                    <div className="text-xs text-gray-400 ml-12">
                                      {vehiculo.marca_local || vehiculo.marca} {vehiculo.modelo_local || vehiculo.modelo} ({vehiculo.año})
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Columna 2: Cliente y Mecánico */}
                              <div className="md:col-span-1 xl:col-span-1 xl:pl-12">
                                <div className="space-y-3">
                                  {/* Cliente */}
                                  <div className="flex items-center">
                                    <div className="mr-3 md:mr-2 bg-green-600 bg-opacity-20 p-2 md:p-1.5 rounded-full">
                                      <FaUser className="text-green-400" size={16} />
                                    </div>
                                    <div>
                                      {cliente ? (
                                        <>
                                          <div className="text-sm font-medium text-white">
                                            {cliente.nombre} {cliente.apellido}
                                          </div>
                                          {cliente.telefono && (
                                            <div className="text-xs text-gray-400">
                                              {cliente.telefono}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="text-sm text-gray-400">
                                          Sin información
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Mecánico */}
                                  <div className="flex items-center">
                                    <div className="mr-3 md:mr-2 bg-purple-600 bg-opacity-20 p-2 md:p-1.5 rounded-full">
                                      <FaUserTie className="text-purple-400" size={14} />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-white">
                                        {mecanicoPrincipal?.nombre || 'Sin asignar'}
                                      </div>
                                      {mantenimiento.mantenimiento_mecanicos?.length > 1 && (
                                        <div className="text-xs text-gray-400">
                                          +{mantenimiento.mantenimiento_mecanicos.length - 1} más
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Columna 3: Fecha y Servicios */}
                              <div className="md:col-span-1 xl:col-span-1 xl:pl-10">
                                <div className="space-y-2">
                                  {/* Fecha */}
                                  <div className="flex items-center">
                                    <div className="mr-2 bg-yellow-600 bg-opacity-20 p-1.5 rounded-full">
                                      <FaCalendar className="text-yellow-400" size={12} />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-white">
                                        {moment(mantenimiento.fecha).format('DD/MM/YYYY')}
                                      </div>
                                      {mantenimiento.hora_finalizacion && (
                                        <div className="text-xs text-gray-400">
                                          {mantenimiento.hora_finalizacion}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Servicios */}
                                  <div className="flex items-center">
                                    <div className="mr-2 bg-purple-600 bg-opacity-20 p-1.5 rounded-full">
                                      <FaTools className="text-purple-400" size={12} />
                                    </div>
                                    <div>
                                      <div className="text-sm text-white">
                                        {mantenimiento.mantenimiento_servicios?.length || 0} servicios
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {mantenimiento.mantenimiento_repuestos?.length > 0 && (
                                          <span>{mantenimiento.mantenimiento_repuestos.length} repuestos</span>
                                        )}
                                        {mantenimiento.mantenimiento_repuestos?.length > 0 && mantenimiento.mantenimiento_items_adicionales?.length > 0 && (
                                          <span> • </span>
                                        )}
                                        {mantenimiento.mantenimiento_items_adicionales?.length > 0 && (
                                          <span>{mantenimiento.mantenimiento_items_adicionales.length} items</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Columna 4: Total */}
                              <div className="md:col-span-2 xl:col-span-1 flex items-center justify-end md:justify-start xl:justify-end">
                                <div className="text-right space-y-1">
                                  {/* Fila 1: Total */}
                                  <div className="text-2xl font-bold text-white">
                                    ${formatPrice(mantenimiento.total)}
                                  </div>
                                  
                                  {/* Fila 2: IVA */}
                                  <div className="text-sm text-gray-400">
                                    IVA incluido
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Botones de acción */}
                          <div className="flex gap-2 ml-8">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerDetalle(mantenimiento);
                              }}
                              className="text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
                              title="Ver detalle"
                            >
                              <FaEye size={14} />
                            </button>
                            
                            {mantenimiento.pdf_base64 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerPDF(mantenimiento);
                                }}
                                className="text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded"
                                title="Ver PDF"
                              >
                                <FaFilePdf size={14} />
                              </button>
                            )}
                            
                            {(isAdmin || isSuperAdmin) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEliminar(mantenimiento);
                                }}
                                className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                                title="Eliminar"
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

      {/* Modal de detalle */}
      <MantenimientoDetailModal
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setMantenimientoSeleccionado(null);
        }}
        mantenimiento={mantenimientoSeleccionado}
      />
    </div>
  );
};

export default MantenimientosPage;